import React from 'react';
import { Query } from '@apollo/client/react/components';
import { mapAfter } from 'apollo-relay/update-query';
import Styled from 'styled';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';

import LoadingProgress from 'lib/react-router/loading-progress';
import ScrollToTop from 'lib/scroll/scroll-to-top';
import observe from 'lib/intersection-observer/observe';

import gaTrack from 'four-nets/lib/google-analytics/track';
import ErrorPage from 'four-nets/errors/error-page';
import FetchMoreButton, { adjacentHrefsFromCursor } from 'four-nets/ui-v2/fetch-more-button/fetch-more-button';
import { ViewerConsumer } from 'four-nets/user/viewer/viewer';

import Masonry, { MediaKey } from '../masonry/masonry';
import defaultStyles from './popular-images.scss';
import query, { PopularImagesQuery, PopularImagesQueryVariables } from './popular-images.graphql';

interface Props {
    styles: typeof defaultStyles;
    columnsSizes?: Map<MediaKey, number>;
    isPartOfOtherPage?: boolean;
    pathname: string;
    first: number;
}

interface State {
    loading?: boolean;
    visible: boolean;
    autoLoadActive: boolean;
}

const DEFAULT_FIRST = 14;

class PopularImages extends React.PureComponent<Props, State> {
    static defaultProps = {
        first: DEFAULT_FIRST,
    };

    fetchMore?: (all: boolean) => Promise<any> = undefined;
    unobserve?: () => void;
    ref: React.RefObject<HTMLDivElement> = React.createRef();
    state = { visible: false, autoLoadActive: false };

    componentDidMount() {
        const element = this.ref && (this.ref.current as Element | null);
        if (element) {
            this.unobserve = observe(
                element,
                () => {
                    this.setState({ visible: true });
                },
                { rootMargin: '300px' }
            );
        }
    }

    componentWillUnmount() {
        if (this.unobserve) {
            this.unobserve();
        }
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        const element = this.ref && (this.ref.current as Element | null);
        if (element && !this.unobserve) {
            this.unobserve = observe(
                element,
                () => {
                    this.setState({ visible: true });
                },
                { rootMargin: '300px' }
            );
        }

        /* When the fetchMore button appeared, we want to fetchMore if autoload is still active */
        const appeared = this.state.visible && !prevState.visible;
        if (this.state.autoLoadActive && appeared && this.fetchMore) {
            this.fetchMore(false);
            this.setState({ visible: false });
        }
    }

    onClick = () => {
        if (this.fetchMore) {
            this.fetchMore(false);

            if (!this.state.autoLoadActive) {
                this.setState({
                    autoLoadActive: true,
                    visible: false,
                });
            }
        }
    };

    render() {
        const { styles, isPartOfOtherPage, columnsSizes, first } = this.props;

        return (
            <ViewerConsumer>
                {({ viewer }) => (
                    <Query<PopularImagesQuery, PopularImagesQueryVariables>
                        variables={{ first, isAuthenticated: Boolean(viewer) }}
                        query={query}
                        notifyOnNetworkStatusChange={true}
                        fetchPolicy='cache-first'
                    >
                        {({ data, loading, error, fetchMore }) => {
                            const hasNextPage =
                                data &&
                                data.popularImages &&
                                data.popularImages.pageInfo &&
                                data.popularImages.pageInfo.hasNextPage;

                            const endCursor = data && data.popularImages && data.popularImages.pageInfo.endCursor;

                            this.fetchMore = hasNextPage
                                ? (all: boolean = false) => {
                                      return fetchMore({
                                          variables: {
                                              after: endCursor,
                                              first: all ? undefined : first,
                                              isAuthenticated: Boolean(viewer),
                                          },
                                          updateQuery: (
                                              previousResult = {} as PopularImagesQuery,
                                              { fetchMoreResult = {} as PopularImagesQuery }
                                          ) => {
                                              return {
                                                  ...previousResult,
                                                  ...fetchMoreResult,
                                                  popularImages: mapAfter(
                                                      previousResult.popularImages,
                                                      fetchMoreResult.popularImages
                                                  ),
                                              } as PopularImagesQuery;
                                          },
                                      }).then(result => {
                                          const { pathname } = this.props;
                                          if (typeof window !== 'undefined') {
                                              const [endCursorHref] = adjacentHrefsFromCursor({
                                                  endCursor,
                                                  pathname,
                                                  pageSize: first,
                                              });
                                              gaTrack('pageview', endCursorHref);
                                          }
                                          return result;
                                      });
                                  }
                                : undefined;
                            return (
                                <LoadingProgress loading={loading}>
                                    <ErrorPage error={error}>
                                        <ScrollToTop />
                                        {data && data.popularImages ? (
                                            <div
                                                className={styles.popularImagesWrapper}
                                                style={{ maxHeight: isPartOfOtherPage ? 820 : undefined }}
                                            >
                                                {isPartOfOtherPage ? (
                                                    <div className={styles.titleRow}>
                                                        <h2 className={styles.title}>
                                                            <FormattedMessage id='POPULAR_IMAGES' />
                                                        </h2>
                                                    </div>
                                                ) : null}
                                                <Masonry
                                                    edges={data.popularImages.edges}
                                                    fetchMoreFn={this.fetchMore}
                                                    columnsSizes={columnsSizes}
                                                    disableLazyLoad={true}
                                                />
                                                <div ref={this.ref}>
                                                    {isPartOfOtherPage ? (
                                                        <div className={styles.footerShowMore}>
                                                            <Link to='/blogs/popular/'>
                                                                <button className={styles.button}>
                                                                    <span className={styles.container}>
                                                                        <FormattedMessage id='BUTTON_SHOW_MORE' />
                                                                    </span>
                                                                </button>
                                                            </Link>
                                                        </div>
                                                    ) : hasNextPage ? (
                                                        <FetchMoreButton
                                                            disabled={loading}
                                                            loading={loading}
                                                            onClick={this.onClick}
                                                        />
                                                    ) : null}
                                                </div>
                                            </div>
                                        ) : null}
                                    </ErrorPage>
                                </LoadingProgress>
                            );
                        }}
                    </Query>
                )}
            </ViewerConsumer>
        );
    }
}
export default Styled(defaultStyles)(PopularImages);
